import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../i18n';
import { useOrder } from '../OrderContext';
import Header from '../components/Header';
import CustomerIcon from '../components/CustomerIcon';

type AiOption = {
  id: 'face' | 'chat';
  iconVariant: 'scan' | 'chat';
  title: string;
  desc: string;
  route: string;
};

const AiPickMenuPage = () => {
  const navigate = useNavigate();
  const { updateOrder } = useOrder();
  const [pressed, setPressed] = useState<string | null>(null);

  // Built per render so the titles/descriptions follow a live language switch.
  const OPTIONS: AiOption[] = [
    { id: 'face', iconVariant: 'scan', title: t.aiPickFaceDetection, desc: t.aiPickFaceDetectionDesc, route: '/ai-pick' },
    { id: 'chat', iconVariant: 'chat', title: t.aiPickChat, desc: t.aiPickChatDesc, route: '/chat' },
  ];

  const handlePick = (opt: AiOption) => {
    updateOrder({ orderMode: 'ai' });
    navigate(opt.route);
  };

  return (
    <div className="kiosk-app rush-tech-bg relative flex h-full w-full flex-col overflow-hidden [animation:pageFadeIn_0.35s_ease_both]">
      <div className="noise-overlay" />
      <Header title={t.aiPickTitle} onBack={() => navigate('/menu-type')} />

      <div className="relative z-10 flex flex-1 flex-col gap-4 px-4 pb-5 pt-3 sm:px-5 sm:pb-6 sm:pt-4">
        {OPTIONS.map((opt, i) => {
          const isPressed = pressed === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              className={`touch-target relative flex flex-1 w-full cursor-pointer flex-col items-center justify-center gap-[clamp(0.9rem,3vh,1.6rem)] overflow-hidden rounded-[22px] border px-6 py-6 text-center transition-all duration-150 [animation:pageFadeIn_0.4s_ease_both] ${
                isPressed
                  ? 'scale-[0.97] border-[var(--gold-lt)] bg-[var(--accent-pressed-bg)] shadow-[0_0_40px_var(--accent-pressed-shadow)]'
                  : 'border-[var(--border)] bg-[var(--card)] shadow-[var(--card-shadow)]'
              }`}
              style={{ animationDelay: `${i * 0.08}s` }}
              onPointerDown={() => setPressed(opt.id)}
              onPointerUp={() => { setPressed(null); handlePick(opt); }}
              onPointerCancel={() => setPressed(null)}
              onPointerLeave={() => setPressed(null)}
            >
              {/* Icon badge */}
              <div
                className={`flex shrink-0 items-center justify-center rounded-full border transition-colors duration-150 [filter:drop-shadow(0_3px_20px_var(--accent-glow))] ${
                  isPressed ? 'border-[var(--gold-lt)] bg-[var(--accent-pressed-bg)]' : 'border-[var(--accent-border)] bg-[var(--accent-bg)]'
                }`}
                style={{ width: 'clamp(5.5rem, 26vw, 7.5rem)', height: 'clamp(5.5rem, 26vw, 7.5rem)' }}
              >
                <CustomerIcon variant={opt.iconVariant} className="block h-[clamp(4rem,18vw,5.4rem)] w-[clamp(4rem,18vw,5.4rem)]" />
              </div>

              {/* Text */}
              <div className="flex flex-col gap-2">
                <div className="font-display font-bold leading-tight text-[var(--cream)]" style={{ fontSize: 'clamp(1.75rem, 6.6vw, 2.5rem)' }}>
                  {opt.title}
                </div>
                <div className="mx-auto max-w-[28ch] text-[var(--muted)]" style={{ fontFamily: "'Sora', sans-serif", fontSize: 'clamp(1.05rem, 3.9vw, 1.3rem)', lineHeight: 1.45 }}>
                  {opt.desc}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AiPickMenuPage;
